import type { Post } from '@/@types/post'
import type { Topic } from '@/@types/topic'
import {
	filterLinkAttachments,
	filterPicAttachments,
	filterVideoAttachments,
} from '@/components/Attachment/utils'
import {
	ADD_TOPPING_SHORTCUT_TOPIC,
	CHANGE_TOPIC_SUBSCRIPTION_STATUS,
	LIST_DRAFT_SUGGESTIONS,
	LIST_SUBSCRIBED_TOPICS,
	LIST_TOPPING_SHORTCUTS,
	TOPIC_DETAIL,
	TOPIC_FEED,
} from '@/constants/api'
import type { Attachment } from '@/utils/fetchers/attachment'
import queryClient from '@/utils/queryClient'
import type { InfiniteData } from '@tanstack/query-core'
import type {
	UseInfiniteQueryOptions,
	UseSuspenseInfiniteQueryOptions,
	UseSuspenseQueryOptions,
} from '@tanstack/react-query'
import { UseQueryOptions } from '@tanstack/react-query'
import { compact } from 'lodash-es'
import { get, post } from '../request'
import { cachePosts, ListPage } from './post'

export const CACHE_TOPICS_QUERY_OPTIONS: UseQueryOptions<Record<string, Topic>> = {
	queryKey: ['TOPICS'],
	initialData: {} as Record<string, Topic>,
}

export function cacheTopic(data: Topic) {
	return queryClient.setQueryData<Record<string, Topic>>(
		CACHE_TOPICS_QUERY_OPTIONS.queryKey,
		(prev) => ({
			...prev,
			[data.id]: data,
		})
	)
}

export function cacheTopics(data: Topic[]) {
	return queryClient.setQueryData<Record<string, Topic>>(
		CACHE_TOPICS_QUERY_OPTIONS.queryKey,
		(prev) => ({
			...prev,
			...Object.fromEntries(data.map((topic) => [topic.id, topic])),
		})
	)
}

export function retrieveTopic(id: string): Topic | undefined {
	return queryClient.getQueryData<Record<string, Topic>>(CACHE_TOPICS_QUERY_OPTIONS.queryKey)?.[id]
}

export function retrieveTopics(ids: string[]): Topic[] {
	return Object.values(
		queryClient.getQueryData<Record<string, Topic>>(CACHE_TOPICS_QUERY_OPTIONS.queryKey) ?? {}
	).filter((topic) => ids.includes(topic.id))
}

export async function getTopicSuggestions(
	content: string,
	attachments: Attachment[]
): Promise<Topic[]> {
	const pictureKeys = compact(filterPicAttachments(attachments).map((item) => item.cdn?.file.key))
	const pictures = pictureKeys.map((item) => ({
		key: item,
		metaData: {},
	}))
	const videoKey = compact(
		filterVideoAttachments(attachments).map((item) => item.cdn?.file.key)
	).slice(0, 1)[0]
	const linkInfo = compact(filterLinkAttachments(attachments).map((item) => item.linkInfo))[0]
	const localTimezone = new Intl.DateTimeFormat().resolvedOptions().timeZone

	const payload = {
		content,
		localTimezone,
		pictures,
		...(linkInfo && {
			linkInfo: {
				linkType: 'default',
				supplementary: false,
				urlsInText: [],
				...linkInfo,
			},
		}),
		...(videoKey && {
			video: {
				isPresent: true,
				key: videoKey,
			},
		}),
		limit: 5,
	}
	const topics = await post(LIST_DRAFT_SUGGESTIONS, payload).then((res) => res.data.topics)
	return topics
}

export async function changeTopicSubscriptionStatus(
	id: string,
	isSubscribe: boolean
): Promise<boolean> {
	return post(CHANGE_TOPIC_SUBSCRIPTION_STATUS, {
		subscribed: isSubscribe,
		push: isSubscribe,
		topicObjectId: id,
	}).then((res) => res.data.success)
}

export async function getTopicDetail(id: string) {
	return get(TOPIC_DETAIL, { params: { id } }).then((res) => res.data.data as Topic)
}

export function getTopicDetailQueryOptions(id: string): UseSuspenseQueryOptions<Topic> {
	return {
		queryKey: [TOPIC_DETAIL, id],
		queryFn: () => getTopicDetail(id),
	}
}

export function getTopicFeedQueryOptions(
	id: string,
	tab: string
): UseInfiniteQueryOptions<ListPage<string>, unknown, InfiniteData<ListPage<string>>> {
	return {
		queryKey: [TOPIC_FEED, id, tab],
		queryFn: async ({ pageParam }) => {
			if (!id || !tab) {
				throw new Error('No valid ID or Tab')
			}
			return post<ListPage<Post>>(
				TOPIC_FEED,
				{
					limit: 20,
					loadMoreKey: pageParam,
					topicId: id,
				},
				{
					params: {
						tab,
					},
				}
			).then((res) => {
				const body = res.data
				cachePosts(body.data)
				return {
					data: body.data.map((post) => post.id),
					loadMoreKey: body.loadMoreKey,
				}
			})
		},
		getNextPageParam: (lastPage: ListPage<string>) => lastPage.loadMoreKey,
		initialPageParam: undefined,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		retryOnMount: false,
	}
}

export function getTopicsToppingQueryOptions(): UseSuspenseQueryOptions<string[]> {
	return {
		queryKey: [LIST_TOPPING_SHORTCUTS],
		queryFn: async () => {
			// 获取置顶话题列表
			const response = await get(LIST_TOPPING_SHORTCUTS)
			const data = response.data.data.shortcuts as Topic[]
			cacheTopics(data)
			return data.map((item) => item.id)
		},
	}
}

export function getSubscribedTopicsQueryOptions(
	username: string
): UseSuspenseInfiniteQueryOptions<ListPage<string>, unknown, InfiniteData<ListPage<string>>> {
	return {
		queryKey: [LIST_SUBSCRIBED_TOPICS, username],
		queryFn: async ({ pageParam }) => {
			return post<ListPage<Topic>>(LIST_SUBSCRIBED_TOPICS, {
				includeRecentPost: 'true',
				filterShortcuts: 'true',
				username,
				sortBy: 'DEFAULT',
				limit: 20,
				loadMoreKey: pageParam,
			}).then((res) => {
				const body = res.data
				cacheTopics(body.data)
				return {
					data: body.data.map((post) => post.id),
					loadMoreKey: body.loadMoreKey,
				}
			})
		},
		getNextPageParam: (lastPage: ListPage<string>) => lastPage.loadMoreKey,
		initialPageParam: undefined,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		retryOnMount: false,
	}
}

export function addToppingShortcutTopic(id: string) {
	return post(ADD_TOPPING_SHORTCUT_TOPIC, {
		id,
	})
}
